import type { LandingPageContent } from './types'

export const PV_MODUL_MONTAGE: LandingPageContent = {
  slug: 'pv-module-montage',
  lang: 'de',
  topicId: 'pv-module-montage',
  title: 'Solarmodule montieren lassen – ohne komplettes System',
  metaDescription:
    'Module schon vorhanden? Fragen Sie nur die Dachmontage an - Handwerksbetriebe geben ' +
    'unverbindlich Angebote ab, Sie vergleichen kostenlos.',
  keywords: [
    'Solarmodule montieren lassen',
    'PV Module Montage Kosten',
    'Dachmontage Photovoltaik',
    'Photovoltaik Montage ohne Material',
    'Solarmodule Montage Handwerker finden',
    'Repowering Photovoltaik',
    'Solarmodule alt gegen neu tauschen',
    'PV-Anlage erweitern',
    'Solarpanel erweitern',
    'zusätzliche Solarmodule',
  ],
  eyebrow: 'Nur Montage, kein Komplettpaket',
  h1: 'Solarmodule montieren lassen – auch ohne komplettes System',
  intro:
    'Nicht jeder braucht ein rundum-Paket: Manche haben Module bereits gekauft oder möchten sie ' +
    'selbst beschaffen und suchen nur einen Handwerksbetrieb für die fachgerechte Dachmontage. Genau ' +
    'dafür gibt es bei Roofora einen eigenen Auftragstyp - "Dachmontage" statt "Komplettsystem mit ' +
    'Montage/Installation".',
  sections: [
    { type: 'heading', text: 'Für wen sich eine reine Dachmontage-Anfrage lohnt' },
    {
      type: 'list',
      items: [
        'Sie haben Module bereits gekauft (z. B. über einen anderen Händler) und brauchen nur die Montage',
        'Sie möchten das Material selbst besorgen, um Kosten zu sparen oder eine bestimmte Marke zu nutzen',
        'Sie planen eine Erweiterung einer bestehenden Anlage und brauchen nur zusätzliche Handwerksleistung',
      ],
    },
    { type: 'heading', text: 'Wer stellt das Material?' },
    {
      type: 'paragraph',
      text:
        'Sie entscheiden bei der Anfrage, ob Sie das Montagematerial selbst stellen oder ob der ' +
        'Handwerksbetrieb es besorgen soll - beide Varianten sind möglich, Sie geben es einfach bei ' +
        'der Anfrage an.',
    },
    { type: 'heading', text: 'So läuft eine Dachmontage-Anfrage ab' },
    {
      type: 'paragraph',
      text:
        'Genau wie bei einer Komplettanfrage stellen Sie eine anonymisierte Anfrage mit Dachbildern, ' +
        'Ausrichtung und gewünschter Modulanzahl bzw. Anlagengröße. Handwerksbetriebe in Ihrer Region ' +
        'sehen die Anfrage und geben unverbindliche Angebote nur für die Montageleistung ab - eine ' +
        'aufwendige Verbrauchs- oder Wirtschaftlichkeitsanalyse entfällt hier, da Sie die ' +
        'Anlagenkonfiguration bereits kennen.',
    },
    { type: 'heading', text: 'Kontakt erst nach Ihrer Freigabe' },
    {
      type: 'paragraph',
      text:
        'Auch bei reinen Montage-Anfragen gilt das gleiche Prinzip wie sonst bei Roofora: Kontakt ' +
        'entsteht erst, wenn Sie einen einzelnen Handwerksbetrieb individuell freigeben - keine ' +
        'Sammelfreigabe, keine Anruf-Flut.',
    },
    { type: 'heading', text: 'Bestandsanlage: Repowering & Erweiterung' },
    {
      type: 'paragraph',
      text:
        'Zwei häufige Anlässe für eine reine Dachmontage-Anfrage betreffen keine neue Anlage, sondern ' +
        'eine bestehende: Repowering (alte Solarmodule gegen neue, leistungsstärkere tauschen) und die ' +
        'Erweiterung um zusätzliche Module. In beiden Fällen geben Sie einfach "Kunde stellt Material" ' +
        'an und bringen die neuen Module selbst mit - der Handwerksbetrieb übernimmt ausschließlich die ' +
        'fachgerechte Montage bzw. den Austausch, ohne dass Sie ein komplettes System neu ausschreiben ' +
        'müssen.',
    },
    {
      type: 'paragraph',
      text:
        'Rechtlicher Hintergrund (externe Information, keine Rechtsberatung): Seit dem Solarpaket I ' +
        '(April 2024) ist der Austausch alter gegen neue, leistungsstärkere Module für Dachanlagen ' +
        'einfacher geworden - die bisherige EEG-Vergütung für die ursprünglich installierte Leistung ' +
        'bleibt in der Regel erhalten, solange die neue Anlage nicht mehr Leistung als die alte bringt. ' +
        'Prüfen Sie die für Ihren Fall geltenden Details direkt bei Ihrem Netzbetreiber oder einer ' +
        'fachkundigen Stelle, bevor Sie planen.',
    },
  ],
  faq: [
    {
      q: 'Kann ich Modultyp und Hersteller frei wählen, wenn ich das Material selbst stelle?',
      a: 'Ja - bei "Kunde stellt Material" bringen Sie die Module Ihrer Wahl mit, der Handwerksbetrieb übernimmt ausschließlich die fachgerechte Montage.',
    },
    {
      q: 'Was, wenn ich noch kein komplettes System habe, sondern erst plane?',
      a: 'Dann ist meist der Auftragstyp "Komplettsystem mit Montage/Installation" passender - der berechnet Ihnen automatisch eine passende Anlagenkonfiguration. Die reine Dachmontage-Anfrage richtet sich an alle, die die Anlagengröße bereits kennen.',
    },
    {
      q: 'Mein Solarmodul ist defekt - kann ich das über Roofora klären?',
      a: 'Das kommt darauf an, worum es geht. Für einen Garantie-/Gewährleistungsfall ist der ursprüngliche Handwerksbetrieb, der die Anlage installiert hat, Ihr Ansprechpartner - das bildet Roofora nicht ab. Wenn Sie stattdessen ein defektes Modul einfach gegen ein neues austauschen lassen möchten (z. B. weil der ursprüngliche Betrieb nicht mehr verfügbar ist oder Sie ein neues Angebot einholen wollen), passt das als reguläre Dachmontage-Anfrage.',
    },
  ],
  primaryCta: { label: 'Dachmontage-Anfrage erstellen', action: 'customer' },
  secondaryCta: { label: 'Nur Elektroinstallation benötigt?', action: 'link', href: '/photovoltaik-elektroinstallation' },
  relatedLinks: [
    { label: 'PV-Installateur finden', href: '/pv-installateur-finden' },
    { label: 'So funktioniert der Angebotsvergleich', href: '/pv-angebot-vergleichen' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
  ],
}
